/* eslint-disable react/forbid-prop-types */
/* eslint-disable react/no-danger */
import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';

import MenuProductDisplayPrice from '@/common/components/MenuProductDisplayPrice';

import forwardRefWithTranslation from '@/common/forwardRefWithTranslation';

const { Order } = Mobi2Go;

export const MenuProductModelPropTypes = {
    id: PropTypes.number.isRequired,
    backend_name: PropTypes.string.isRequired,
    tags: PropTypes.arrayOf(PropTypes.string).isRequired,
    image_src: PropTypes.string,
    name: PropTypes.string.isRequired,
    display_price: PropTypes.string,
    description: PropTypes.string.isRequired,
    hasTag: PropTypes.func.isRequired,
    getSizeOptions: PropTypes.func.isRequired,
    getPrice: PropTypes.func.isRequired,
};

class MenuProduct extends React.Component {
    static propTypes = {
        menu_product: PropTypes.shape(MenuProductModelPropTypes).isRequired,
        min_height: PropTypes.number,
        className: PropTypes.string,
        component: PropTypes.object,
        onClick: PropTypes.func,
        onCustomiseClick: PropTypes.func,
        t: PropTypes.func.isRequired,
    };

    static defaultProps = {
        min_height: null,
        onClick: null,
        onCustomiseClick: null,
        className: '',
        component: null
    };

    constructor(...args) {
        super(...args);

        this.state = {
            selected_size: null,
        };
    }

    getPrice = () => {
        const { menu_product } = this.props;
        const productPrice = menu_product.getPrice();
        return productPrice;
    };

    getDOMNode = () => this.dom_node;

    onSizeSelectorClick = e => e.stopPropagation();

    onClick = e => {
        const { menu_product, onClick } = this.props;
        const { selected_size } = this.state;

        if (!onClick) {
            return;
        }

        // Prevent links in HTML description from triggering add
        if (e.target.tagName === 'A') {
            return;
        }

        const is_restricted = Order.isProductRestricted(menu_product);
        if (is_restricted) {
            return;
        }

        onClick({
            menu_product,
            selected_size,
        });
    };

    onKeyDown = e => {
        if (e.keyCode !== 13) {
            return;
        }

        this.onClick(e);
    };

    onCustomiseButtonClick = e => {
        e.stopPropagation();

        const { menu_product, onCustomiseClick } = this.props;
        const { selected_size } = this.state;

        if (!onCustomiseClick) {
            return;
        }

        onCustomiseClick({
            menu_product,
            selected_size,
        });
    };

    hasSizes = () => {
        const { menu_product } = this.props;
        return (
            menu_product.hasTag('display-size-options') &&
            menu_product.getSizeOptions().length > 1
        );
    };

    getSelectedSize = () => {
        const { selected_size } = this.state;
        const { menu_product } = this.props;

        if (this.hasSizes()) {
            return selected_size || menu_product.getSizeOptions()[0].name;
        }
        return null;
    };

    setSelectedSize = e => {
        this.setState({
            selected_size: e.target.value,
        });
    };

    renderPriceComponent = () => {
        const { menu_product, t } = this.props;
        const { display_price } = menu_product;

        if (display_price) {
            return display_price.split(',').length > 1 ? (
                <div className="m2g-menu-product-price m2g-menu-product-price--display-price m2g-menu-product-price--multiple-parts">
                    {display_price.split(',').map(price => (
                        <div
                            key={price}
                            className="m2g-menu-product-price-part"
                        >
                            {price}
                        </div>
                    ))}
                </div>
            ) : (
                <MenuProductDisplayPrice product={menu_product} />
            );
        }

        if(this.getPrice().toFloat() <= 0) {
            return null;
        }

        return (
            <span className="m2g-menu-product-price">
                {t('common:price', { price: this.getPrice() })}
            </span>
        );
    };

    render() {
        const { menu_product, className, component, min_height, onClick, t } = this.props;
        const {
            id,
            backend_name,
            tags,
            image_src,
            name,
            description,
        } = menu_product;
        const is_sold_out = Order.isProductSoldOut(menu_product);
        const is_hidden = Order.isProductHidden(menu_product);

        if (is_hidden) {
            return null;
        }

        return (
            <div
                id={`m2g-menu-product-${backend_name}`}
                role="button"
                tabIndex="0"
                className={`m2g-menu-product-container ${className}`}
                data-product={id}
                data-product-name={backend_name}
                data-in-stock={!is_sold_out}
                data-has-sizes={this.hasSizes()}
                data-tags={tags.join(' ')}
                onClick={this.onClick}
                onKeyDown={this.onKeyDown}
            >
                <div
                    data-in-stock={!is_sold_out}
                    className={`m2g-menu-product${
                        image_src ? ' m2g-menu-product--with-image' : ''
                    }${is_sold_out ? ' m2g-menu-product--out-of-stock' : ''}`}
                    tabIndex="0"
                    role="button"
                    style={{
                        minHeight: min_height,
                    }}
                    ref={e => {
                        this.dom_node = e;
                    }}
                >
                    {image_src ? (
                        <div className="m2g-menu-product-image-container">
                            <div className="m2g-menu-product-image-loading-placeholder m2g-hidden" />
                            <img
                                alt={name}
                                className="m2g-menu-product-image m2g-image"
                                src={image_src}
                            />
                        </div>
                    ) : (
                        <div className="m2g-menu-product-image-container">
                            <div className="m2g-menu-product-image m2g-image m2g-image--placeholder" />
                        </div>
                    )}

                    <div className="m2g-menu-product-info">
                        <span
                            className="m2g-menu-product-name"
                            dangerouslySetInnerHTML={{ __html: name }}
                        />
                        {this.renderPriceComponent()}

                        {is_sold_out && (
                            <div className="m2g-menu-product-out-of-stock-badge m2g-badge">
                                {t('sold_out')}
                            </div>
                        )}

                        <span
                            className="m2g-menu-product-description m2g-text--offset m2g-custom-html"
                            dangerouslySetInnerHTML={{ __html: description }}
                        />
                    </div>

                    {onClick && (
                        <button
                            type="button"
                            onKeyDown={() => {}}
                            className={`m2g-menu-product-add-button m2g-button ${
                                is_sold_out
                                    ? 'm2g-button--disabled'
                                    : 'm2g-button--primary m2g-button--add-button'
                            }`}
                            tabIndex="-1"
                        >
                            {is_sold_out ? (
                                <span className="m2g-menu-product-add-button-text m2g-menu-product-add-button-text--sold-out">
                                    {t('sold_out')}
                                </span>
                            ) : (
                                [
                                    <span
                                        key="long"
                                        className="m2g-menu-product-add-button-text m2g-menu-product-add-button-text--long"
                                    >
                                        {t('add_to_order')}
                                    </span>,
                                    <span
                                        key="short"
                                        className="m2g-menu-product-add-button-text m2g-menu-product-add-button-text--short m2g-hidden"
                                    >
                                        {t('add')}
                                    </span>,
                                ]
                            )}
                        </button>
                    )}

                    {!is_sold_out &&
                        menu_product.getModifierGroups().getLength() > 0 && (
                            <button
                                type="button"
                                onKeyDown={() => {}}
                                data-customise
                                className="m2g-menu-product-customise-button m2g-button m2g-hidden"
                                tabIndex="-1"
                                onClick={this.onCustomiseButtonClick}
                            >
                                <span className="m2g-menu-product-customise-button-text">
                                    {t('customise')}
                                </span>
                                <span className="m2g-menu-product-customise-button-text m2g-menu-product-customise-button-text--alt m2g-hidden">
                                    Customize
                                </span>
                            </button>
                        )}

                    {this.hasSizes() && (
                        <div className="m2g-menu-product-size-options">
                            <div
                                className="m2g-menu-product-size-options-input-container m2g-input-container m2g-form-element-group"
                                data-icon-after="select"
                            >
                                <select
                                    data-size-options
                                    className="m2g-menu-product-size-options-select m2g-select m2g-select--primary m2g-form-element--full-width"
                                    value={this.getSelectedSize()}
                                    onChange={this.setSelectedSize}
                                    onClick={this.onSizeSelectorClick}
                                >
                                    {_.map(
                                        menu_product.getSizeOptions(),
                                        size_option => (
                                            <option
                                                key={size_option.name}
                                                value={size_option.name}
                                            >
                                                {size_option.name}&nbsp;–&nbsp;
                                                {t('common:price', {
                                                    price: size_option.price,
                                                })}
                                            </option>
                                        )
                                    )}
                                </select>
                            </div>
                        </div>
                    )}

                    <div className="m2g-menu-product-tags">
                        {_.map(tags, (tag, index) => (
                            <div
                                key={index}
                                className="m2g-menu-product-tag m2g-tag"
                                data-tag={tag}
                                title={tag}
                            />
                        ))}
                    </div>

                    {component}
                </div>
            </div>
        );
    }
}

export default forwardRefWithTranslation('menu.products')(MenuProduct);
